import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useParams } from "react-router-dom";
import { useNavigateWithCommandClick } from "../../hooks/useNavigateWithCommandClick";
import { formatDateWithTime } from "../../utils/dateFormatting";

import Button from "../../components/Button";
import { Table, Row, Column } from "../../components/Table";
import {
  LoadingState,
  EmptyState,
  ErrorState
} from "../../components/CommonStates";
import { Header } from "../../components/AdminTab";
import { Dropdown } from "../../components/Dropdown";
import { Input } from "../../components/Input";

function ChatLogsTab({
  onError,
  onSuccess,
  domainFilter,
  uuidFilterParam,
  botIdParam
}) {
  const { domain } = useParams();
  const [displayedLogs, setDisplayedLogs] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [filterChanging, setFilterChanging] = useState(false);
  const [usernameSearching, setUsernameSearching] = useState(false); // Track username search loading
  const [chatError, setChatError] = useState("");
  const [creatingIndex, setCreatingIndex] = useState(false); // Track LlamaCloud index creation
  const isProgrammaticChange = useRef(false);
  const isProgrammaticBotChange = useRef(false);
  const [selectedDomain, setSelectedDomain] = useState(() => {
    return domainFilter || domain || "";
  });
  const [selectedUuidFilter, setSelectedUuidFilter] = useState(() => {
    return uuidFilterParam || "all";
  }); // all, internal, external
  const [specificUsername, setSpecificUsername] = useState(() => {
    // Initialize username from URL if domain is specific-user
    const initialDomain = domainFilter || domain || "";
    return initialDomain === "specific-user" && uuidFilterParam
      ? uuidFilterParam
      : "";
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [hasFetchedBot, setHasFetchedBot] = useState(false);
  const [selectedBotId, setSelectedBotId] = useState(() => {
    // Initialize bot ID from URL if domain is specific-bot
    const initialDomain = domainFilter || domain || "";
    return initialDomain === "specific-bot" && uuidFilterParam
      ? uuidFilterParam
      : "";
  });
  const [selectedUuidBotId, setSelectedUuidBotId] = useState(() => {
    // Initialize bot ID from URL path param if domain is uuid-only
    const initialDomain = domainFilter || domain || "";
    return initialDomain === "uuid-only" && botIdParam ? botIdParam : "";
  }); // Bot filter for uuid-only mode
  const [availableDomains, setAvailableDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [availableBots, setAvailableBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [chatPagination, setChatPagination] = useState({
    total: 0,
    limit: 100,
    offset: 0,
    hasMore: false
  });

  const navigate = useNavigateWithCommandClick();

  // Fetch available domains from server
  const fetchAvailableDomains = useCallback(async () => {
    if (domainsLoading || availableDomains.length > 0) return;

    setDomainsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.adminChats}/domains`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableDomains(data.domains || []);
      }
    } catch (err) {
      // Silently fail - domains dropdown will be empty but functional
      console.error("Failed to fetch available domains:", err);
    } finally {
      setDomainsLoading(false);
    }
  }, [domainsLoading, availableDomains.length]);

  // Fetch available bots from server
  const fetchAvailableBots = useCallback(async () => {
    if (botsLoading || availableBots.length > 0) return;

    setBotsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminBots, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableBots(data.bots || []);
      }
    } catch (err) {
      // Silently fail - bots dropdown will be empty but functional
      console.error("Failed to fetch available bots:", err);
    } finally {
      setBotsLoading(false);
    }
  }, [botsLoading, availableBots.length]);

  const fetchChatLogs = useCallback(
    async (opts = {}) => {
      setChatLoading(true);
      setChatError("");
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams({
          limit: opts.limit ?? chatPagination.limit,
          offset: opts.offset ?? chatPagination.offset
        });

        // Add domain filter if selected
        // Use 'in' operator to check if property was explicitly provided (even if undefined/null)
        const domainFilter =
          "usernameDomain" in opts ? opts.usernameDomain : selectedDomain;
        const uuidFilter =
          "uuidFilter" in opts ? opts.uuidFilter : selectedUuidFilter;

        // CRITICAL FIX: For username and botId, ONLY use state values if they match the current domain
        // This prevents stale values from being used when switching between filter types
        let username, botId, uuidBotId;

        if ("username" in opts) {
          username = opts.username;
        } else {
          // Only use state username if we're actually in specific-user mode
          username =
            domainFilter === "specific-user" ? specificUsername : undefined;
        }

        if ("botId" in opts) {
          botId = opts.botId;
        } else {
          // Only use state botId if we're actually in specific-bot mode
          botId = domainFilter === "specific-bot" ? selectedBotId : undefined;
        }

        if ("uuidBotId" in opts) {
          uuidBotId = opts.uuidBotId;
        } else {
          // Only use state uuidBotId if we're actually in uuid-only mode
          uuidBotId =
            domainFilter === "uuid-only" ? selectedUuidBotId : undefined;
        }

        // If specific bot filter is selected and a bot is chosen, add botId parameter
        // If botId is empty, it means "All" is selected, so don't filter by bot
        if (domainFilter === "specific-bot") {
          if (botId) {
            params.append("botId", botId);
          }
          // If botId is empty (All), don't add any filter - will show all bots
        } else if (domainFilter === "specific-user") {
          // For specific-user, ONLY add username param, never botId
          if (username) {
            params.append("username", username);
          }
        } else if (domainFilter === "external-only") {
          params.append("excludeUsernameDomain", "joinpropel.com");
        } else if (domainFilter === "uuid-only") {
          // Handle UUID filtering with sub-filter
          if (uuidFilter === "internal") {
            params.append("internalUuidsOnly", "true");
          } else if (uuidFilter === "external") {
            params.append("externalUuidsOnly", "true");
          } else {
            // All UUIDs
            params.append("uuidUsernamesOnly", "true");
          }
          // Add bot filter if selected for uuid-only mode
          if (uuidBotId) {
            params.append("botId", uuidBotId);
          }
        } else if (domainFilter) {
          params.append("usernameDomain", domainFilter);
        }

        const response = await fetch(
          `${API_ENDPOINTS.adminChats}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        const data = await response.json();
        if (response.ok) {
          const logs = data.threads || [];

          // Update displayed logs immediately if not changing filters
          if (!filterChanging) {
            setDisplayedLogs(logs);
          }

          setChatPagination(
            data.pagination || {
              total: 0,
              limit: 100,
              offset: 0,
              hasMore: false
            }
          );

          // Return the logs for use in filter changes
          return logs;
        } else {
          setChatError(data.error || "Failed to fetch chat logs");
          return null;
        }
      } catch (err) {
        setChatError("Network error. Please try again.");
        return null;
      } finally {
        setChatLoading(false);
      }
    },
    [
      chatPagination.limit,
      chatPagination.offset,
      selectedDomain,
      selectedUuidFilter,
      specificUsername,
      selectedBotId,
      selectedUuidBotId,
      filterChanging
    ]
  );

  // Separate function for filter changes that returns a promise
  const fetchChatLogsWithFilterChange = useCallback(
    (opts = {}) => {
      return fetchChatLogs(opts);
    },
    [fetchChatLogs]
  );

  const handleDomainFilterChange = async (event) => {
    const newDomain = event.target.value;

    // Don't do anything if the value hasn't actually changed
    if (newDomain === selectedDomain) {
      return;
    }

    // Mark as programmatic change to prevent useEffect from overriding
    isProgrammaticChange.current = true;

    // Clear displayed logs immediately to prevent showing stale data
    setDisplayedLogs([]);

    // Update selectedDomain immediately to prevent dropdown flash
    // This ensures the dropdown shows the selected option right away
    setSelectedDomain(newDomain);

    // Reset UUID filter when changing domain
    setSelectedUuidFilter("all");

    // Reset pagination when changing any filter
    setChatPagination({
      total: 0,
      limit: 100,
      offset: 0,
      hasMore: false
    });

    // ALWAYS clear both username and botId when switching filter types
    // This prevents stale values from being used
    setSpecificUsername("");
    setSelectedBotId("");
    setSelectedUuidBotId(""); // Also clear UUID bot filter

    // For specific-user mode, immediately set hasSearched to true
    // This allows results to display when we fetch all users
    if (newDomain === "specific-user") {
      setHasSearched(true);
    } else {
      setHasSearched(false);
    }

    setHasFetchedBot(false);

    // For specific-bot mode or uuid-only mode, ensure bots are loaded
    if (newDomain === "specific-bot" || newDomain === "uuid-only") {
      fetchAvailableBots();
    }

    // Set loading states after updating selection
    setFilterChanging(true);

    // Update URL based on domain selection
    if (newDomain) {
      navigate(`/admin/chat-logs/${newDomain}`, { replace: true });
    } else {
      navigate("/admin/chat-logs", { replace: true });
    }

    // Reset pagination when filtering
    // Fetch chat logs for all filter types
    if (newDomain !== "specific-bot") {
      // For all modes except specific-bot, fetch immediately
      try {
        const newLogs = await fetchChatLogsWithFilterChange({
          limit: chatPagination.limit,
          offset: 0,
          usernameDomain: newDomain === "specific-user" ? "" : newDomain, // Empty domain for "all users"
          uuidFilter: "all",
          botId: undefined, // Explicitly clear botId when switching away from specific-bot
          username: undefined, // Explicitly clear username when switching away from specific-user
          uuidBotId: undefined // Explicitly clear uuidBotId when switching domains
        });

        // Update displayed logs with the new data
        if (newLogs) {
          setDisplayedLogs(newLogs);
        }
      } finally {
        setFilterChanging(false);
      }
    } else if (newDomain === "specific-bot") {
      // For specific-bot, clear loading state
      // The useEffect will handle fetching logs for "All" once bots are loaded
      setFilterChanging(false);
    }
  };

  const handleUuidFilterChange = async (event) => {
    const newUuidFilter = event.target.value;

    // Don't do anything if the value hasn't actually changed
    if (newUuidFilter === selectedUuidFilter) {
      return;
    }

    // Update selectedUuidFilter immediately to prevent dropdown flash
    setSelectedUuidFilter(newUuidFilter);

    // Reset pagination when changing filter
    setChatPagination({
      total: 0,
      limit: 100,
      offset: 0,
      hasMore: false
    });

    // Set loading state
    setFilterChanging(true);

    // Update URL based on UUID filter selection
    // Include bot ID as path param if selected
    let path;
    if (newUuidFilter === "all") {
      path = selectedUuidBotId
        ? `/admin/chat-logs/${selectedDomain}/all/${selectedUuidBotId}`
        : `/admin/chat-logs/${selectedDomain}`;
    } else {
      path = selectedUuidBotId
        ? `/admin/chat-logs/${selectedDomain}/${newUuidFilter}/${selectedUuidBotId}`
        : `/admin/chat-logs/${selectedDomain}/${newUuidFilter}`;
    }
    navigate(path, { replace: true });

    // Reset pagination when filtering
    try {
      const newLogs = await fetchChatLogsWithFilterChange({
        limit: chatPagination.limit,
        offset: 0,
        uuidFilter: newUuidFilter,
        botId: undefined, // Explicitly clear botId for specific-bot mode
        username: undefined, // Explicitly clear username
        uuidBotId: selectedUuidBotId // Maintain bot filter when changing UUID filter
      });

      // Update displayed logs with the new data
      if (newLogs) {
        setDisplayedLogs(newLogs);
      }
    } finally {
      setFilterChanging(false);
    }
  };

  const handleUuidBotFilterChange = async (event) => {
    const newBotId = event.target.value;

    // Don't do anything if the value hasn't actually changed
    if (newBotId === selectedUuidBotId) {
      return;
    }

    // Update selectedUuidBotId immediately
    setSelectedUuidBotId(newBotId);

    // Reset pagination when changing filter
    setChatPagination({
      total: 0,
      limit: 100,
      offset: 0,
      hasMore: false
    });

    // Set loading state
    setFilterChanging(true);

    // Update URL based on bot selection
    let path;
    if (selectedUuidFilter === "all") {
      path = newBotId
        ? `/admin/chat-logs/${selectedDomain}/all/${newBotId}`
        : `/admin/chat-logs/${selectedDomain}`;
    } else {
      path = newBotId
        ? `/admin/chat-logs/${selectedDomain}/${selectedUuidFilter}/${newBotId}`
        : `/admin/chat-logs/${selectedDomain}/${selectedUuidFilter}`;
    }
    navigate(path, { replace: true });

    // Reset pagination when filtering
    try {
      const newLogs = await fetchChatLogsWithFilterChange({
        limit: chatPagination.limit,
        offset: 0,
        uuidFilter: selectedUuidFilter,
        usernameDomain: "uuid-only",
        botId: undefined, // Explicitly clear botId for specific-bot mode
        username: undefined, // Explicitly clear username
        uuidBotId: newBotId // Pass the new bot ID explicitly
      });

      // Update displayed logs with the new data
      if (newLogs) {
        setDisplayedLogs(newLogs);
      }
    } finally {
      setFilterChanging(false);
    }
  };

  const handleUsernameSearch = async () => {
    setUsernameSearching(true);
    setHasSearched(true);

    // Reset pagination when searching
    setChatPagination({
      total: 0,
      limit: 100,
      offset: 0,
      hasMore: false
    });

    const trimmedUsername = specificUsername.trim();

    // Update URL based on whether username is provided
    if (trimmedUsername) {
      navigate(
        `/admin/chat-logs/specific-user/${encodeURIComponent(trimmedUsername)}`,
        { replace: true }
      );
    } else {
      // No username - show all users
      navigate("/admin/chat-logs/specific-user", { replace: true });
    }

    try {
      const newLogs = await fetchChatLogsWithFilterChange({
        limit: chatPagination.limit,
        offset: 0,
        usernameDomain: trimmedUsername ? "specific-user" : "", // Empty for all users
        username: trimmedUsername || undefined,
        botId: undefined, // Explicitly clear botId
        uuidBotId: undefined // Explicitly clear uuidBotId
      });

      // Update displayed logs with the new data
      if (newLogs) {
        setDisplayedLogs(newLogs);
      }
    } finally {
      setUsernameSearching(false);
    }
  };

  const handleBotFilterChange = async (event) => {
    const newBotId = event.target.value;

    // Don't do anything if the value hasn't actually changed
    if (newBotId === selectedBotId) {
      return;
    }

    // Mark as programmatic change to prevent useEffect from also fetching
    isProgrammaticBotChange.current = true;

    // Update selectedBotId immediately
    setSelectedBotId(newBotId);
    setHasFetchedBot(true);

    // Reset pagination when changing filter
    setChatPagination({
      total: 0,
      limit: 100,
      offset: 0,
      hasMore: false
    });

    // Set loading state
    setFilterChanging(true);

    // Update URL based on bot selection
    // If "All" is selected (empty), just go to /admin/chat-logs/specific-bot
    if (newBotId) {
      navigate(`/admin/chat-logs/specific-bot/${newBotId}`, { replace: true });
    } else {
      navigate("/admin/chat-logs/specific-bot", { replace: true });
    }

    // Reset pagination when filtering
    // Pass empty botId for "All", or specific botId for a selected bot
    try {
      const newLogs = await fetchChatLogsWithFilterChange({
        limit: chatPagination.limit,
        offset: 0,
        usernameDomain: "specific-bot",
        botId: newBotId, // Can be empty for "All"
        username: undefined, // Explicitly clear username
        uuidBotId: undefined // Explicitly clear uuidBotId
      });

      // Update displayed logs with the new data
      if (newLogs) {
        setDisplayedLogs(newLogs);
      }
    } finally {
      setFilterChanging(false);
      // Reset the flag after a small delay to ensure useEffects have run
      setTimeout(() => {
        isProgrammaticBotChange.current = false;
      }, 100);
    }
  };

  const handleCreateLlamaCloudIndex = async () => {
    setCreatingIndex(true);

    try {
      const token = localStorage.getItem("token");
      const requestBody = {};

      // Add username or botId based on current filter
      if (selectedDomain === "specific-user") {
        requestBody.username = specificUsername;
      } else if (selectedDomain === "specific-bot") {
        requestBody.botId = selectedBotId;
      } else if (selectedDomain === "uuid-only" && selectedUuidBotId) {
        requestBody.botId = selectedUuidBotId;
      }

      const response = await fetch(API_ENDPOINTS.createLlamaCloudIndex, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        if (onSuccess) {
          // Display the message with instructions
          onSuccess(
            data.message ||
              `File uploaded successfully! File ID: ${data.fileId}`
          );
        }
      } else {
        if (onError) {
          onError(data.error || "Failed to create LlamaCloud index");
        }
      }
    } catch (error) {
      console.error("Error creating LlamaCloud index:", error);
      if (onError) {
        onError("Network error. Please try again.");
      }
    } finally {
      setCreatingIndex(false);
    }
  };

  // Update selectedDomain when URL changes (from external navigation)
  useEffect(() => {
    const urlDomain = domainFilter || domain || "";

    // If this is a programmatic change (from dropdown), skip the update
    // and just reset the flag
    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }

    // Only update if the URL domain is different from selected domain
    if (urlDomain !== selectedDomain) {
      setSelectedDomain(urlDomain);
      // When switching to uuid-only domain, ensure UUID filter is properly initialized
      if (urlDomain === "uuid-only" && !uuidFilterParam) {
        setSelectedUuidFilter("all");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainFilter, domain]);

  // Update selectedUuidBotId when URL path parameter changes
  useEffect(() => {
    const urlDomain = domainFilter || domain || "";
    const botIdFromUrl = botIdParam || "";

    // Only update if we're in uuid-only mode and the bot ID has changed
    if (urlDomain === "uuid-only" && botIdFromUrl !== selectedUuidBotId) {
      setSelectedUuidBotId(botIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botIdParam, domainFilter, domain]);

  // Update selectedUuidFilter when URL parameter changes
  useEffect(() => {
    const urlUuidFilter = uuidFilterParam || "all";
    const urlDomain = domainFilter || domain || "";

    // CRITICAL: Don't trigger fetches from this effect for specific-user/specific-bot
    // These modes are handled by their respective handlers
    if (urlDomain === "specific-user" && !uuidFilterParam) {
      // In specific-user mode without username - don't auto-fetch
      // User needs to click "Show All" or enter a username and Search
      return;
    }

    if (urlDomain === "specific-bot" && !uuidFilterParam) {
      // In specific-bot mode without botId - don't auto-fetch
      // The other useEffect will handle "All" bots case
      return;
    }

    // Handle specific-user domain differently
    if (urlDomain === "specific-user" && uuidFilterParam) {
      // For specific-user, uuidFilterParam is the username from URL
      // Update username state to match URL
      setSpecificUsername(uuidFilterParam);
      setHasSearched(true);

      // Fetch chat logs for this specific user
      fetchChatLogs({
        limit: chatPagination.limit,
        offset: 0,
        usernameDomain: "specific-user",
        username: uuidFilterParam,
        botId: undefined, // Explicitly clear botId for specific-user
        uuidBotId: undefined // Explicitly clear uuidBotId
      });
    } else if (urlDomain === "specific-bot" && uuidFilterParam) {
      // If this is a programmatic change (from dropdown), skip the fetch
      if (isProgrammaticBotChange.current) {
        isProgrammaticBotChange.current = false;
        return;
      }

      // For specific-bot, uuidFilterParam is the bot ID
      // Update bot ID state if different
      if (uuidFilterParam !== selectedBotId) {
        setSelectedBotId(uuidFilterParam);
      }
      // Trigger fetch when we have a bot ID and haven't fetched for it yet
      if (!hasFetchedBot || selectedBotId !== uuidFilterParam) {
        setHasFetchedBot(true);
        fetchChatLogs({
          limit: chatPagination.limit,
          offset: 0,
          usernameDomain: "specific-bot",
          botId: uuidFilterParam,
          username: undefined, // Explicitly clear username for specific-bot
          uuidBotId: undefined // Explicitly clear uuidBotId
        });
      }
    } else if (
      urlUuidFilter !== selectedUuidFilter &&
      urlDomain !== "specific-user" &&
      urlDomain !== "specific-bot"
    ) {
      setSelectedUuidFilter(urlUuidFilter);
      // Force a re-fetch when UUID filter changes from URL
      // But ONLY if we're in uuid-only mode (not specific-user or specific-bot)
      if (urlDomain === "uuid-only") {
        fetchChatLogs({
          limit: chatPagination.limit,
          offset: 0,
          uuidFilter: urlUuidFilter
          // Don't explicitly set botId, username, or uuidBotId - let them be determined from state
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uuidFilterParam,
    selectedUuidFilter,
    selectedDomain,
    // Removed specificUsername from dependencies - we don't want typing to trigger this effect
    selectedBotId,
    domainFilter,
    domain,
    chatPagination.limit,
    hasSearched,
    hasFetchedBot
  ]);

  // Fetch all bots when in specific-bot mode with no botId selected (All)
  useEffect(() => {
    // Get the current URL domain to ensure we're actually in specific-bot mode
    const urlDomain = domainFilter || domain || "";

    // Skip if this is a programmatic bot change from the dropdown
    if (isProgrammaticBotChange.current) {
      return;
    }

    if (
      urlDomain === "specific-bot" &&
      selectedDomain === "specific-bot" &&
      !selectedBotId &&
      !hasFetchedBot &&
      !botsLoading
    ) {
      setHasFetchedBot(true);

      // Fetch logs for all bots
      fetchChatLogs({
        limit: chatPagination.limit,
        offset: 0,
        usernameDomain: "specific-bot",
        botId: "", // Empty botId means show all
        username: undefined, // Explicitly clear username
        uuidBotId: undefined // Explicitly clear uuidBotId
      });
    }
  }, [
    selectedDomain,
    selectedBotId,
    hasFetchedBot,
    botsLoading,
    fetchChatLogs,
    chatPagination.limit,
    domainFilter,
    domain
  ]);

  // Initialize domains, bots, and fetch initial data
  useEffect(() => {
    // Fetch available domains and bots (only once on mount)
    fetchAvailableDomains();
    fetchAvailableBots();

    // Fetch initial chat logs with proper filter parameters
    const initializeData = async () => {
      const initialDomain = domainFilter || domain || "";
      const initialUuidFilter = uuidFilterParam || "all";

      // Skip initial fetch for specific-user with username or specific-bot with botId
      // Will be handled by URL param effect
      if (
        (initialDomain === "specific-user" && uuidFilterParam) ||
        (initialDomain === "specific-bot" && uuidFilterParam)
      ) {
        return;
      }

      // Skip initial fetch for specific-bot without botId - will auto-select first bot
      if (initialDomain === "specific-bot" && !uuidFilterParam) {
        return;
      }

      const initialLogs = await fetchChatLogs({
        limit: 100,
        offset: 0,
        usernameDomain: initialDomain,
        uuidFilter: initialUuidFilter,
        botId: undefined, // Explicitly clear botId
        username: undefined, // Explicitly clear username
        uuidBotId: undefined // Explicitly clear uuidBotId
      });
      if (initialLogs) {
        setDisplayedLogs(initialLogs);
      }
    };
    initializeData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount

  return (
    <>
      {chatError && <ErrorState>{chatError}</ErrorState>}

      <Header>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            flexWrap: "wrap"
          }}
        >
          <Dropdown
            id="domain-filter"
            value={selectedDomain}
            onChange={handleDomainFilterChange}
            disabled={domainsLoading || filterChanging || usernameSearching}
          >
            <option value="">Anyone</option>
            <option value="joinpropel.com">Internal employees</option>
            <option value="external-only">External partners</option>
            <option value="uuid-only">Propel app users</option>
            <option value="specific-user">Specific user</option>
            <option value="specific-bot">Specific bot</option>
            <option disabled> </option>
            <option disabled>External partners</option>

            {availableDomains
              .filter((domain) => domain !== "joinpropel.com")
              .map((domain) => (
                <option key={domain} value={domain}>
                  {domain}
                </option>
              ))}
          </Dropdown>

          {selectedDomain === "uuid-only" && (
            <>
              <Dropdown
                id="uuid-filter"
                value={selectedUuidFilter}
                onChange={handleUuidFilterChange}
                disabled={filterChanging || usernameSearching}
              >
                <option value="all">All UUIDs</option>
                <option value="internal">Internal UUIDs only</option>
                <option value="external">External UUIDs only</option>
              </Dropdown>
              <Dropdown
                id="uuid-bot-filter"
                value={selectedUuidBotId}
                onChange={handleUuidBotFilterChange}
                disabled={filterChanging || usernameSearching || botsLoading}
              >
                <option value="">All bots</option>
                {availableBots.map((bot) => (
                  <option key={bot.id} value={bot.id}>
                    {bot.name}
                  </option>
                ))}
              </Dropdown>
            </>
          )}

          {selectedDomain === "specific-user" && (
            <div
              style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}
            >
              <Input
                type="text"
                placeholder="Enter username..."
                value={specificUsername}
                onChange={(e) => setSpecificUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUsernameSearch();
                  }
                }}
                disabled={filterChanging || usernameSearching}
                style={{ width: "300px" }}
              />
              <Button
                onClick={handleUsernameSearch}
                disabled={filterChanging || usernameSearching}
              >
                Search
              </Button>
              {hasSearched && specificUsername && (
                <Button
                  variant="secondary"
                  onClick={async () => {
                    setSpecificUsername("");
                    setUsernameSearching(true);

                    // Update URL to remove username
                    navigate("/admin/chat-logs/specific-user", {
                      replace: true
                    });

                    // Reset pagination
                    setChatPagination({
                      total: 0,
                      limit: 100,
                      offset: 0,
                      hasMore: false
                    });

                    try {
                      // Fetch all users' chat logs
                      const newLogs = await fetchChatLogsWithFilterChange({
                        limit: chatPagination.limit,
                        offset: 0,
                        usernameDomain: "", // Empty for all users
                        username: undefined,
                        botId: undefined,
                        uuidBotId: undefined
                      });

                      if (newLogs) {
                        setDisplayedLogs(newLogs);
                      }
                    } finally {
                      setUsernameSearching(false);
                    }
                  }}
                  disabled={filterChanging || usernameSearching}
                >
                  Clear
                </Button>
              )}
            </div>
          )}

          {selectedDomain === "specific-bot" && (
            <Dropdown
              id="bot-filter"
              value={selectedBotId}
              onChange={handleBotFilterChange}
              disabled={filterChanging || usernameSearching || botsLoading}
            >
              <option value="">All</option>
              {availableBots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
            </Dropdown>
          )}
        </div>

        {/* LlamaCloud Index Creation Button */}
        {((selectedDomain === "specific-user" && hasSearched) ||
          (selectedDomain === "specific-bot" && selectedBotId) ||
          (selectedDomain === "uuid-only" && selectedUuidBotId)) &&
          displayedLogs.length > 0 && (
            <Button
              onClick={handleCreateLlamaCloudIndex}
              disabled={creatingIndex || filterChanging || usernameSearching}
            >
              {creatingIndex ? "Creating..." : "Create index"}
            </Button>
          )}
      </Header>

      {chatLoading &&
      !displayedLogs.length &&
      !filterChanging &&
      !usernameSearching ? (
        <LoadingState>Loading chat logs...</LoadingState>
      ) : displayedLogs.length === 0 &&
        !filterChanging &&
        !usernameSearching &&
        (selectedDomain !== "specific-user" || hasSearched) ? (
        <EmptyState>
          <h4>No chat logs found</h4>
          <p>
            {selectedDomain
              ? selectedDomain === "external-only"
                ? "No external chat threads found (excluding @joinpropel.com)."
                : selectedDomain === "uuid-only"
                ? selectedUuidFilter === "internal"
                  ? "No internal UUID chat threads found."
                  : selectedUuidFilter === "external"
                  ? "No external UUID chat threads found."
                  : "No chat threads found with UUID usernames."
                : selectedDomain === "specific-user"
                ? specificUsername.trim()
                  ? `No chat threads found for username "${specificUsername}".`
                  : "No chat threads found."
                : selectedDomain === "specific-bot"
                ? selectedBotId
                  ? `No chat threads found for the selected bot.`
                  : "No chat threads found."
                : `No chat threads found for domain @${selectedDomain}.`
              : "There are no chat threads to display."}
          </p>
        </EmptyState>
      ) : displayedLogs.length > 0 ? (
        <div
          style={{
            position: "relative",
            opacity: filterChanging || usernameSearching ? 0.25 : 1
          }}
        >
          <Table columns="1.5fr 1fr 1fr">
            <Row>
              <Column>Username</Column>
              <Column>Bot</Column>
              <Column>Most recent message</Column>
            </Row>
            {displayedLogs.map((thread) => (
              <Row
                key={thread.threadId}
                style={{
                  cursor:
                    filterChanging || usernameSearching ? "default" : "pointer"
                }}
                onClick={(event) =>
                  !(filterChanging || usernameSearching) &&
                  navigate(`/admin/thread/${thread.threadId}`, {}, event)
                }
                title={
                  filterChanging || usernameSearching
                    ? ""
                    : "Click to view full thread"
                }
              >
                <Column data-label="Username">{thread.username}</Column>
                <Column data-label="Bot name">{thread.botName}</Column>
                <Column data-label="Most recent message">
                  {formatDateWithTime(thread.lastMessageAt)}
                </Column>
              </Row>
            ))}
          </Table>
        </div>
      ) : null}

      {/* Pagination Controls */}
      {chatPagination.total > chatPagination.limit && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "1rem",
            marginTop: "1rem"
          }}
        >
          <Button
            onClick={() =>
              fetchChatLogs({
                limit: chatPagination.limit,
                offset: Math.max(
                  0,
                  chatPagination.offset - chatPagination.limit
                )
              })
            }
            disabled={
              chatLoading ||
              filterChanging ||
              usernameSearching ||
              chatPagination.offset === 0
            }
          >
            Prev
          </Button>
          <Button
            onClick={() =>
              fetchChatLogs({
                limit: chatPagination.limit,
                offset: chatPagination.offset + chatPagination.limit
              })
            }
            disabled={
              chatLoading ||
              filterChanging ||
              usernameSearching ||
              !chatPagination.hasMore
            }
          >
            Next
          </Button>
          <span style={{ alignSelf: "center", color: "#555" }}>
            Showing {chatPagination.offset + 1} -{" "}
            {Math.min(
              chatPagination.offset + chatPagination.limit,
              chatPagination.total
            )}{" "}
            of {chatPagination.total}
          </span>
        </div>
      )}
    </>
  );
}

export default ChatLogsTab;
